import useHasAccess from "@hooks/useHasAccess"

import { findCoveringSubscription, findItemSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"

import { Skeleton } from "@particles/index"

import { Button, Hint } from "@atoms/index"

import {
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
    useUnsubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import { ItemSubscriptionCTAProps } from "./ItemSubscriptionCTA.types"

export default function ItemSubscriptionCTA(props: ItemSubscriptionCTAProps) {
    const { catalogId, item } = props

    const { hasPermission } = useHasAccess()
    const hasPricebookEditPermission = hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)

    const subscriptionsQuery = useCatalogSubscriptionsQuery(catalogId)
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeMutation()

    // Coverage is derived off the subscriptions listing rather than baked onto the item detail; the
    // subscribe/unsubscribe mutations invalidate that shared cache, so this CTA re-derives on its own.
    const subscriptions = subscriptionsQuery.data ?? []
    const coveringSubscription = findCoveringSubscription(subscriptions, {
        id: item.id,
        brand: item.brand?.id ?? null,
        categories: item.categories,
    })
    const itemSubscription = findItemSubscription(subscriptions, item.id)

    const handleSubscribe = () => {
        subscribeMutation.mutate({ catalog: catalogId, level: "item", catalog_item: item.id })
    }

    const handleUnsubscribe = () => {
        if (itemSubscription) {
            unsubscribeMutation.mutate(itemSubscription)
        }
    }

    if (!hasPricebookEditPermission) {
        return null
    }

    if (subscriptionsQuery.isLoading) {
        return <Skeleton height={32} width={110} />
    } else if (coveringSubscription === null) {
        if (item.is_active) {
            return (
                <Button
                    variant="solid"
                    size="sm"
                    leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
                    onClick={handleSubscribe}
                    isProcessing={subscribeMutation.isPending}
                    isDisabled={subscribeMutation.isPending}
                >
                    Subscribe
                </Button>
            )
        } else {
            return (
                <Hint
                    colorScheme="blue"
                    body="This item is no longer in the catalog, so it can't be subscribed to."
                    defaultPlacement="bottom"
                >
                    <Button
                        variant="solid"
                        size="sm"
                        leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
                        onClick={() => undefined}
                        isDisabled={true}
                    >
                        Subscribe
                    </Button>
                </Hint>
            )
        }
    } else if (coveringSubscription.level === "item") {
        return (
            <Button
                variant="subtle"
                size="sm"
                onClick={handleUnsubscribe}
                isProcessing={unsubscribeMutation.isPending}
                isDisabled={unsubscribeMutation.isPending}
            >
                Unsubscribe
            </Button>
        )
    } else {
        return null
    }
}
